import { useState } from 'react';
import clsx from 'clsx';
import { usePopper } from 'react-popper';
import ReactDOM from 'react-dom';

// Hooks
import useToggle from '@/utils/hooks/utils/useToggle';

// Libs
import { formatAddedSocialProfile } from '@/lib/socialProfile';

// Components
import CustomPopover from '@/components/composite/Popover/CustomPopover';
import SpOrSpGroupAvatar from '@/components/composite/SpGroups/SpOrSpGroupAvatar';
import Tooltip from '@/components/base/Tooltip';

// Types
import type { SocialProfileType } from '@/types/socialProfileType';
import type { SpGroupType } from '@/utils/hooks/swr/group/useSPGroups';

const AdditionalSocialProfilesAndGroupsCondensedList = ({
    additionalProfiles,
    forceLightMode = false,
    profileNetworkType = true
}: {
    additionalProfiles: (SocialProfileType | SpGroupType)[];
    forceLightMode?: boolean;
    profileNetworkType?: boolean;
}) => (
    <div className="scrollbar flex max-h-64 w-40 flex-col space-y-3 overflow-auto p-1">
        {additionalProfiles?.map((item) => (
            <div className="flex items-center space-x-3.5" key={item?.id}>
                <SpOrSpGroupAvatar
                    size="sm"
                    item={item}
                    forceLightMode={forceLightMode}
                    profileNetworkType={profileNetworkType}
                />

                <div className="flex flex-col items-start space-y-0.5 overflow-hidden">
                    <span
                        className={clsx('truncate font-GSemiBold text-xs', {
                            'text-icoBlack': forceLightMode
                        })}>
                        {'name' in item ? item.name : formatAddedSocialProfile(item)?.main_name}
                    </span>

                    {'external_api_data' in item && formatAddedSocialProfile(item)?.sub_name ? (
                        <span className="truncate text-xxs text-icoGray-500 dark:text-icoDarkMode-wolf">
                            {formatAddedSocialProfile(item)?.sub_name}
                        </span>
                    ) : null}
                </div>
            </div>
        ))}
    </div>
);

export type SocialProfilesAndGroupsCondensedListProps = {
    list: ((SocialProfileType | SpGroupType) & { avatarTooltip?: string })[];
    maxDisplayed?: number;
    className?: string;
    forceLightMode?: boolean;
    size?: 'base' | 'sm';
    condensed?: string;
    profileNetworkType?: boolean;
};

const SocialProfilesAndGroupsCondensedList = ({
    list,
    maxDisplayed = 3,
    className,
    forceLightMode = false,
    size = 'sm',
    condensed = null,
    profileNetworkType = true
}: SocialProfilesAndGroupsCondensedListProps) => {
    const { isOpen: isPopoverOpen, toggle: togglePopover } = useToggle();
    const [referenceElement, setReferenceElement] = useState<HTMLButtonElement>();
    const [popperElement, setPopperElement] = useState<HTMLDivElement>();
    const { styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: 'bottom-start',
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: [-5, 8]
                }
            }
        ]
    });

    if (!list?.length) {
        return null;
    }

    let displayed = list?.slice(0, maxDisplayed - 1);
    const inPopover = list?.slice(maxDisplayed - 1);

    if (inPopover.length === 1) {
        displayed = [...displayed, ...inPopover];
    }

    return (
        <div
            className={clsx('relative z-2 flex flex-row items-center', className)}
            data-testid="list-avatars">
            <div className="z-1 flex flex-row-reverse">
                {displayed?.reverse().map((filteredItem, index) => (
                    <Tooltip content={filteredItem?.avatarTooltip ?? null} key={filteredItem.id}>
                        <div className="flex items-center">
                            <SpOrSpGroupAvatar
                                forceLightMode={forceLightMode}
                                key={filteredItem.id}
                                item={filteredItem}
                                size={size ?? (list?.length === 1 ? 'base' : 'sm')}
                                profileNetworkType={profileNetworkType}
                                className={clsx({
                                    '-ml-2': !condensed && index !== displayed.length - 1,
                                    [condensed]: condensed && index !== displayed.length - 1
                                })}
                                data-testid="list-avatar"
                            />
                        </div>
                    </Tooltip>
                ))}
            </div>
            {inPopover?.length > 1 ? (
                <>
                    <button
                        type="button"
                        ref={setReferenceElement}
                        onClick={togglePopover}
                        className={clsx(
                            '-ml-2 flex items-center justify-center rounded-full bg-white p-0.5',
                            {
                                'h-8 w-8 text-xxs': size === 'sm',
                                'h-10.5 w-10.5 text-sm': size === 'base',
                                'dark:bg-icoDarkMode-anthracite': !forceLightMode
                            }
                        )}>
                        <span
                            className={clsx(
                                'inline-flex h-full w-full items-center justify-center rounded-full bg-icoGray-100 text-icoBlue',
                                { 'dark:bg-icoDarkMode-deepBlack': !forceLightMode }
                            )}>
                            <span>{`+ ${inPopover?.length}`}</span>
                        </span>
                    </button>
                    {isPopoverOpen
                        ? ReactDOM.createPortal(
                              <CustomPopover
                                  hasBtnClose={false}
                                  hasArrow={false}
                                  wrapperClassName="z-20"
                                  popperElement={popperElement}
                                  setPopperElement={setPopperElement}
                                  styles={{
                                      popper: styles.popper
                                  }}
                                  width="w-48"
                                  toggle={togglePopover}
                                  attributes={attributes}
                                  data-testid="custom-popover">
                                  <div
                                      className={clsx(
                                          'flex w-48 flex-col space-y-0.5 rounded-m bg-white p-1.5',
                                          {
                                              'dark:bg-icoDarkMode-anthracite': !forceLightMode
                                          }
                                      )}>
                                      <AdditionalSocialProfilesAndGroupsCondensedList
                                          additionalProfiles={inPopover}
                                          forceLightMode={forceLightMode}
                                          profileNetworkType={profileNetworkType}
                                      />
                                  </div>
                              </CustomPopover>,
                              document.body
                          )
                        : null}
                </>
            ) : null}
        </div>
    );
};

export default SocialProfilesAndGroupsCondensedList;
